import useSWR, { KeyedMutator } from 'swr'
import { useCallback, useState, useMemo, useEffect } from 'react'
import { captureException } from '@sentry/nextjs'
import { useRouter } from 'next/router'

import { useAuth } from '../useAuth'
import { getCookie } from '../../utils/cookies'
import useCategoryDataStructure from '../../components/hooks/useCategoryDataStructure'
import { getCountryAndLocaleStrings, getCurrencyCode } from '../../utils/locales'
import { VertexAIRecommendationModelType, VertexAIUserEventType } from '../../utils/constants'
import { IAimeosResponse } from '../../utils/types/aimeosApi'
import { Product, ProductText } from '../../utils/types/Product'
import useInternalTraffic from '../../components/hooks/useInternalTraffic'
import { RecommendationsResponse } from '../../utils/nextApis/product-recommendations/types'

// Define the hook parameters
export interface ProductSuggestionsParams {
  filter?: string
  categoryFilter?: string
  supplierFilter?: string
  associatedItems?: Array<string | [string, number]>
  pageCategories?: string | string[]
  pageSize?: number
  cartId?: string
  purchaseTransaction?: {
    currencyCode: string
    revenue: number
  }
  defaultEventType?: VertexAIUserEventType
  modelType: VertexAIRecommendationModelType
  isInternalTraffic?: boolean
  enableExperimentTracking?: boolean
}

interface Recommendations {
  data: Product[] | undefined
  isLoading: boolean
  error: Error | undefined
  triggerEvent: (
    eventType: VertexAIUserEventType,
    triggerRequestParams?: ProductSuggestionsParams,
  ) => Promise<void>
  mutate: KeyedMutator<RecommendationsResponse>
}

type ContractPriceList = Record<string, { percent: string, volume: string, key: string }[]>

/**
 * Gets a persistent client ID, first trying the GA cookie,
 * then falling back to localStorage for consistency across sessions
 * even when cookies are unavailable.
 *
 * The GA client ID format after extraction is: XXXXXXXXXX.XXXXXXXXXX
 * We'll match this format for our fallback IDs for consistency with Vertex AI
 */
const getClientId = (): string => {
  // Try to get GA client ID first
  const gaClientId = getCookie('_ga')?.[1]?.substring(6)
  if (gaClientId) {
    return gaClientId
  }

  // If no GA cookie, try to get from localStorage
  try {
    let persistentId = localStorage.getItem('vertex_visitor_id')

    // If no ID in localStorage, generate a new one in GA format
    if (!persistentId) {
      // Generate two random numbers to match GA format XXXXXXXXXX.XXXXXXXXXX
      const timestamp = Math.floor(Date.now() / 1000)
      const random = Math.floor(Math.random() * 1000000000)

      // Format as GA client ID format: XXXXXXXXXX.XXXXXXXXXX
      persistentId = `${timestamp}.${random}`
      localStorage.setItem('vertex_visitor_id', persistentId)
    }

    return persistentId
  } catch (e) {
    // If localStorage fails, generate a temporary ID in GA format
    const timestamp = Math.floor(Date.now() / 1000)
    const random = Math.floor(Math.random() * 1000000000)
    return `${timestamp}.${random}`
  }
}

// Create a fetcher function for the API
const fetcher = async (
  apiUrl: string,
  params: any,
): Promise<RecommendationsResponse> => {
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (!response.ok) {
      const errorData = await response.json()
      const errorMessage = errorData.error || 'An error occurred while fetching recommendations'
      const fetchError = new Error(errorMessage)

      // Capture the error with Sentry
      captureException(fetchError, {
        level: 'error',
        tags: { component: 'useRecommendationsAPI', function: 'fetcher' },
        extra: {
          status: response.status,
          statusText: response.statusText,
          params,
          location: params.location,
        },
      })

      throw fetchError
    }

    return await response.json()
  } catch (error) {
    // Capture any unexpected errors
    captureException(error, {
      level: 'error',
      tags: { component: 'useRecommendationsAPI', function: 'fetcher' },
      extra: { params },
    })
    throw error
  }
}

/**
 * Hook for fetching recommendations from the product-recommendations API
 *
 * @param params Parameters for the hook
 * @returns Result containing recommendations data and functions to trigger events
 */
const useRecommendationsAPI = (
  params: ProductSuggestionsParams,
): Recommendations => {
  const {
    filter,
    categoryFilter,
    supplierFilter,
    associatedItems = [],
    pageSize = 100,
    cartId,
    purchaseTransaction,
    defaultEventType = 'home-page-view',
    modelType,
    enableExperimentTracking = true,
  } = params

  const [eventParams, setEventParams] = useState<typeof requestParams | null>(null)

  const { user } = useAuth()
  const { asPath, locale: countryAndLangString } = useRouter()
  const { subCategory } = useCategoryDataStructure()
  const { country, locale } = getCountryAndLocaleStrings(countryAndLangString)
  const currency = getCurrencyCode(country)
  const { isInternalTraffic } = useInternalTraffic()

  const clientId = getClientId()

  const {
    data: contractPrices,
    mutate: mutatePrices,
  } = useSWR<IAimeosResponse<ContractPriceList>>(user ? '/api/v1/price' : null)

  useEffect(() => {
    // Refresh contract prices when user changes
    mutatePrices()
  }, [user, mutatePrices])

  const requestParams = {
    clientId,
    modelType,
    eventType: defaultEventType,
    filter,
    categoryFilter,
    supplierFilter,
    associatedItems,
    pageSize,
    cartId,
    purchaseTransaction,
    location: asPath,
    isInternalTraffic,
    enableExperimentTracking,
  }

  // Create a stable key that won't change between renders
  const stableKey = useMemo(() => {
    // If no event has been triggered yet, return null to prevent fetching
    if (!eventParams) {
      return null
    }

    return [
      '/api/product-recommendations',
      eventParams,
    ]
  }, [eventParams])

  const swrConfig = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  }

  const { data, error, mutate } = useSWR<RecommendationsResponse, Error>(
    stableKey,
    fetcher,
    swrConfig,
  )

  // Function to trigger a specific event
  const triggerEvent = useCallback(async (
    eventType: VertexAIUserEventType,
    triggerRequestParams?: ProductSuggestionsParams,
  ) => {
    // Create the event-specific params
    const newEventParams = {
      ...requestParams,
      ...triggerRequestParams, // Override initial request params with trigger request params
      eventType,
      isInternalTraffic, // Ensure internal traffic status is included in every event
    }

    if (!clientId) {
      console.error('Missing required parameter: clientId')
      captureException(new Error('Missing required parameter: clientId'), {
        level: 'warning',
        tags: { component: 'useRecommendationsAPI', eventType, location: asPath },
        extra: { newEventParams },
      })
      return
    }

    // Validate event-specific required parameters and handle errors gracefully
    if (eventType === 'category-page-view' && !categoryFilter) {
      const categoryError = new Error('Missing required parameter: pageCategories for category-page-view event')
      console.error(categoryError.message)
      captureException(categoryError, {
        level: 'error',
        tags: { component: 'useRecommendationsAPI', eventType, location: asPath },
        extra: { newEventParams },
      })
      return // Return instead of throwing to prevent client crash
    }

    if (eventType === 'purchase-complete' && !purchaseTransaction) {
      const purchaseError = new Error(
        'Missing required parameter: purchaseTransaction for purchase-complete event',
      )
      console.error(purchaseError.message)
      captureException(purchaseError, {
        level: 'error',
        tags: { component: 'useRecommendationsAPI', eventType },
        extra: { newEventParams },
      })
      return // Return instead of throwing to prevent client crash
    }

    // Update state to trigger data refresh via useSWR
    setEventParams(newEventParams)
  }, [
    requestParams,
    mutate,
    clientId,
    categoryFilter,
    cartId,
    purchaseTransaction,
    isInternalTraffic,
    enableExperimentTracking,
  ])

  const products: Product[] | undefined = data?.results?.hits
    ?.map(({ document: hit }) => ({
      id: hit.id,
      'product.id': hit.id,
      'product.code': hit.sku,
      'product.label': hit.texts.en.name,
      'product.type': hit['variant-skus']?.length ? 'select' : 'default',
      'product.url': '',
      'product.status': 1,
      'product.config': [],
      'product.scale': '1',
      'product.target': '',
      'product.rating': '0.00',
      'product.ratings': 0,
      'product.ctime': hit.ctime,
      'product.highlight': hit.highlight,
      'product.sales-unit': hit.salesUnit,
      'product.referenceUnit': hit.referenceUnit,
      'product/property': [
        {
          id: '',
          'product.property.id': '',
          'product.property.type': 'per-box',
          'product.property.value': String(hit.perBox),
          'product.property.languageid': null,
        },
        {
          id: '',
          'product.property.id': '',
          'product.property.type': 'per-carton',
          'product.property.value': String(hit.perCarton),
          'product.property.languageid': null,
        },
      ],
      attribute: [],
      catalog: subCategory.flatMap((category) => (
        category['catalog.label'] === hit.categories.one[locale]
          ? [category].concat(category.catalog?.flatMap((categoryTwo) => (
            categoryTwo['catalog.label'] === hit.categories.two[locale]
              ? [categoryTwo].concat(categoryTwo.catalog?.flatMap((categoryThree) => (
                categoryThree['catalog.label'] === hit.categories.three[locale] ? [categoryThree] : []
              )) || []) : []
          )) || []) : []
      )),
      media: [{
        id: '',
        'media.previews': { 1280: hit.image },
        'media.type': 'default',
        'media.languageid': null,
        'media.mimetype': '',
        'media.domain': 'product',
        'media.label': hit['texts.en.name'],
        'media.preview': hit.image,
        'media.url': hit.image,

      }],
      price: [
        {
          id: '',
          'price.type': 'default',
          'price.label': hit.texts.en.name,
          'price.currencyid': currency,
          'price.domain': 'product',
          'price.quantity': 1,
          'price.value': hit.price[currency],
          'price.rebate': '0',
          'price.costs': 0,
          'price.taxrate': 25.5,
        },
        ...(contractPrices?.data?.[hit.sku] ? contractPrices?.data?.[hit.sku].map((price) => ({
          id: '',
          'price.type': 'default',
          'price.label': `custom_discount/${price.key}`,
          'price.currencyid': currency,
          'price.domain': 'product',
          'price.quantity': Number(price.volume),
          'price.value': String(((Number(hit.price[currency]) * 0.75) * (1 - (Number(price.percent) / 100))) / 0.75),
          'price.rebate': '0',
          'price.costs': 0,
          'price.taxrate': 25.5,
        })) : []),
      ],
      stock: [],
      supplier: [{
        id: '',
        'supplier.id': '',
        'supplier.code': hit.supplier,
        'supplier.label': hit.supplier,
        'supplier.status': 1,
        'service.config': hit['delivery-details'],
        'service.price': hit['delivery-costs'],
        product: [],
      }],
      text: Object.entries(hit.texts).flatMap<ProductText>(([lang, texts]) => {
        if (typeof texts === 'string') {
          if (['brand', 'model'].includes(lang)) {
            return [{
              'text.id': '',
              id: '',
              'text.label': lang,
              'text.type': lang as ProductText['text.type'],
              'text.content': texts,
              'text.languageid': null,
              'text.domain': 'product',
            }]
          }
        }
        if (lang === locale) {
          return Object.entries(texts).map(([type, content]) => ({
            'text.id': '',
            id: '',
            'text.label': type,
            'text.type': type as ProductText['text.type'],
            'text.content': content,
            'text.languageid': lang,
            'text.domain': 'product',
          }))
        }
        return []
      }),
      product: hit['variant-skus']?.map((variantSku, index) => ({
        id: '',
        'product.id': '',
        'product.code': variantSku,
        'product.label': hit.variance?.[index] || `Variant ${variantSku}`,
        'product.type': 'default',
        'product.url': '',
        'product.status': 1,
        'product.config': [],
        'product.scale': '1',
        'product.target': '',
        'product.rating': '0.00',
        'product.ratings': 0,
        attribute: [
          ...(hit.variance?.[index] ? [{
            id: '',
            'attribute.id': '',
            'attribute.languageid': null,
            'attribute.type': 'size',
            'attribute.code': hit.variance?.[index],
            'attribute.label': hit.variance?.[index],
            'attribute.domain': 'product',
            'product.lists.type': 'variant',
          }] : []),
        ],
        'product/property': [],
        catalog: [],
        text: [],
        media: [],
        price: [],
        supplier: [],
        stock: [],
      })) || [],
    }))

  return {
    data: products,
    isLoading: !error && !data && eventParams !== null,
    error,
    triggerEvent,
    mutate,
  }
}

export default useRecommendationsAPI
